import { useState } from "react";
import { Globe, Copy, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useWorld } from "@/context/WorldContext";

export const ShareDialog = () => {
  const { world, reload } = useWorld();
  const [busy, setBusy] = useState(false);
  const link = world.share_token ? `${window.location.origin}/share/${world.share_token}` : "";

  const toggle = async (v) => {
    setBusy(true);
    try {
      await api.post(`/worlds/${world.id}/share`, { is_public: v });
      await reload();
      toast.success(v ? "Public link enabled" : "World is private again");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Share link copied"); };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full border-line bg-transparent" data-testid="share-world-btn">
          {world.is_public ? <Globe className="w-4 h-4 text-emerald-400" /> : <Lock className="w-4 h-4" />}
          <span className="hidden sm:inline">Share</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-panel border-line" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-amber-100">Share this world</DialogTitle>
          <DialogDescription>Anyone with the link can read your wiki. Only you can edit.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-lg border border-line p-4">
          <div>
            <p className="text-sm text-slate-200">Public read-only link</p>
            <p className="text-xs text-slate-500">{world.is_public ? "Visible to anyone with the link" : "Private to you"}</p>
          </div>
          <Switch checked={world.is_public} disabled={busy} onCheckedChange={toggle} data-testid="share-toggle" />
        </div>
        {world.is_public && link && (
          <div className="flex gap-2">
            <Input readOnly value={link} className="font-code text-xs" data-testid="share-link-input" />
            <Button onClick={copy} className="bg-amber-500 hover:bg-amber-400 text-onaccent" data-testid="copy-share-link-btn"><Copy className="w-4 h-4" /></Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
